import { Occurrence } from "../../../../../models/occurrence";

/**
 * Mapeia campos para IDs de containers (para seções expandíveis)
 */
const fieldToContainerId: Record<string, string> = {
  'failures': 'failures-container',
  'modules': 'modules-container',
  'partOrders': 'part-orders-container',
  'partOrderParts': 'part-order-parts-container',
  'dealershipDn': 'dealershipDn-container',
};

/**
 * Encontra o primeiro campo obrigatório faltante e retorna seu ID
 */
export const getFirstMissingFieldId = (
  requiredFields: string[],
  occurrence: Occurrence
): string | null => {
  for (const field of requiredFields) {
    // Valida campos de array
    if (field === 'failures') {
      if (!occurrence.failures || occurrence.failures.length === 0) {
        return 'failures';
      }
      // Valida se todas as falhas têm failureType preenchido
      const incompleteFaitures = occurrence.failures.filter(failure =>
        failure.failureType === undefined || failure.failureType === null || failure.failureType === "undefined"
      );
      if (incompleteFaitures.length > 0) {
        return 'failures';
      }
      continue;
    }

    if (field === 'modules') {
      if (!occurrence.modules || occurrence.modules.length === 0) {
        return 'modules';
      }
      continue;
    }

    if (field === 'partOrders') {
      if (!occurrence.partOrders || occurrence.partOrders.length === 0) {
        return 'partOrders';
      }
      continue;
    }

    if (field === 'partOrderParts') {
      if (!occurrence.partOrderParts || occurrence.partOrderParts.length === 0) {
        return 'partOrderParts';
      }
      continue;
    }

    // Valida campos normais
    const value = occurrence[field as keyof Occurrence];
    if (value === undefined || value === null || value === '') {
      return field;
    }
  }

  return null;
};

/**
 * Rola a página até o primeiro campo obrigatório faltante
 * Tratamento especial para seções expandíveis (falhas, peças, etc)
 */
export const scrollToFirstMissingField = (
  requiredFields: string[],
  occurrence: Occurrence,
  container?: HTMLElement | null
): void => {
  const firstMissingFieldId = getFirstMissingFieldId(requiredFields, occurrence);

  if (!firstMissingFieldId) return;

  // Aguarda um tempo para garantir que o DOM foi atualizado
  setTimeout(() => {
    // Primeiro, tenta encontrar o container da seção expandível
    const containerId = fieldToContainerId[firstMissingFieldId];
    let element = containerId ? document.getElementById(containerId) : null;

    // Se não encontrar pelo container, tenta pelo ID do campo direto
    if (!element) {
      element = document.getElementById(firstMissingFieldId);
    }

    // Se não encontrar por ID, tenta por input/select com name correspondente
    if (!element) {
      element = document.querySelector(`[id="${firstMissingFieldId}"]`);
    }

    if (!element) {
      element = document.querySelector(`input[id="${firstMissingFieldId}"], select[id="${firstMissingFieldId}"]`);
    }

    // Fallback: procura por data attributes ou classNames
    if (!element && containerId) {
      element = document.querySelector(`[data-field="${firstMissingFieldId}"]`);
    }

    if (element) {
      // Se há um container específico (como sidebar), rola dentro dele
      if (container) {
        const elementTop = element.getBoundingClientRect().top;
        const containerTop = container.getBoundingClientRect().top;
        const scrollPosition = container.scrollTop + (elementTop - containerTop) - 100; // 100px de margem
        container.scrollTop = Math.max(0, scrollPosition);
      } else {
        // Caso contrário, rola a página
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Foca no elemento se possível (para inputs/selects)
      const focusableElement = element.querySelector('input, select, button, [tabindex]') as HTMLElement;
      if (focusableElement && 'focus' in focusableElement) {
        focusableElement.focus();
      } else if ('focus' in element) {
        (element as HTMLElement).focus();
      }
    }
  }, 100);
};
